import React from 'react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { StatusDot } from './StatusDot';
import type { StatusDotType } from './StatusDot';
import type { TimesheetEntry } from '@/types/timesheet';
import { Calendar, User, Clock, FileText } from 'lucide-react';
import { format } from 'date-fns';

interface BookingHoverCardProps {
  booking: TimesheetEntry;
  statusType: StatusDotType;
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
}

export const BookingHoverCard: React.FC<BookingHoverCardProps> = ({
  booking,
  statusType,
  children,
  onOpenChange,
}) => {
  const totalScheduledHours = booking.scheduledHours
    ? (booking.scheduledHours.monday || 0) +
      (booking.scheduledHours.tuesday || 0) +
      (booking.scheduledHours.wednesday || 0) +
      (booking.scheduledHours.thursday || 0) +
      (booking.scheduledHours.friday || 0)
    : 0;

  const totalActualHours =
    (booking.hours.monday || 0) +
    (booking.hours.tuesday || 0) +
    (booking.hours.wednesday || 0) +
    (booking.hours.thursday || 0) +
    (booking.hours.friday || 0);

  const isEdited = totalActualHours !== totalScheduledHours;

  return (
    <HoverCard openDelay={200} closeDelay={100} onOpenChange={onOpenChange}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        className="w-80 p-4"
        side="right"
        align="start"
        sideOffset={5}
      >
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-start gap-2">
            <StatusDot type={statusType} size="md" className="mt-1.5" />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm leading-tight">
                Project ID: {booking.projectId}
              </h4>
              <p className="text-xs text-muted-foreground mt-0.5">
                Cost Centre: {booking.costCentreId}
              </p>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 text-xs">
            <div
              className={`px-2 py-1 rounded-md font-medium ${
                statusType === 'scheduled'
                  ? 'bg-blue-100 text-blue-700'
                  : statusType === 'edited'
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {statusType === 'scheduled' && 'From Schedule'}
              {statusType === 'edited' && 'From Schedule (edited)'}
              {statusType === 'dismissed' && 'Dismissed'}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t" />

          {/* Schedule Details */}
          {!booking.isDismissed && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-xs">
                <Clock className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-muted-foreground">Scheduled hours</p>
                  <p className="font-medium">
                    {totalScheduledHours}h total
                    {totalScheduledHours > 0 && booking.scheduledHours && (
                      <span className="text-muted-foreground ml-1">
                        (
                        {[
                          booking.scheduledHours.monday && 'Mon',
                          booking.scheduledHours.tuesday && 'Tue',
                          booking.scheduledHours.wednesday && 'Wed',
                          booking.scheduledHours.thursday && 'Thu',
                          booking.scheduledHours.friday && 'Fri',
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        )
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {isEdited && (
                <div className="flex items-start gap-2 text-xs">
                  <Clock className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-muted-foreground">Your hours</p>
                    <p className="font-medium">
                      {totalActualHours}h total
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Created By */}
          {booking.bookingCreatedByName && (
            <>
              <div className="border-t" />
              <div className="flex items-start gap-2 text-xs">
                <User className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-muted-foreground">Created by</p>
                  <p className="font-medium">{booking.bookingCreatedByName}</p>
                </div>
              </div>
            </>
          )}

          {/* Created Date */}
          {booking.bookingCreatedAt && (
            <div className="flex items-start gap-2 text-xs">
              <Calendar className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-muted-foreground">Created on</p>
                <p className="font-medium">
                  {format(new Date(booking.bookingCreatedAt), 'dd MMM yyyy')}
                </p>
              </div>
            </div>
          )}

          {/* Notes */}
          {booking.bookingNotes && (
            <>
              <div className="border-t" />
              <div className="flex items-start gap-2 text-xs">
                <FileText className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-muted-foreground">Notes</p>
                  <p className="font-medium">{booking.bookingNotes}</p>
                </div>
              </div>
            </>
          )}

          {/* Dismissal Info */}
          {booking.isDismissed && booking.dismissalReason && (
            <>
              <div className="border-t" />
              <div className="flex items-start gap-2 text-xs">
                <FileText className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-muted-foreground">Dismissal reason</p>
                  <p className="font-medium">{booking.dismissalReason}</p>
                </div>
              </div>
            </>
          )}

          {/* Footer hint */}
          <div className="border-t pt-2">
            <p className="text-xs text-muted-foreground text-center">
              Click the menu (⋮) for more actions
            </p>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export default BookingHoverCard;
